import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, UploadCloud, Loader2, Star } from "lucide-react";
import { CATEGORIES } from "@/lib/business";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const imgSrc = (img) => (!img ? "" : img.startsWith("http") ? img : `${BACKEND}${img}`);
const blank = { name: "", price: "", category: "mains", description: "", image: "", popular: false };

export const MenuManager = ({ pass }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState(blank);

  const H = { headers: { "X-Admin-Passcode": pass } };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/menu`, H);
      setItems(data.sort((a, b) => a.category.localeCompare(b.category) || a.name.localeCompare(b.name)));
    } catch { toast.error("Could not load menu."); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setField = (id, field, value) =>
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

  const setOpt = (id, idx, field, value) =>
    setItems((arr) => arr.map((x) => x.id === id ? { ...x, options: x.options.map((o, i) => i === idx ? { ...o, [field]: value } : o) } : x));
  const addOpt = (id) =>
    setItems((arr) => arr.map((x) => x.id === id ? { ...x, options: [...(x.options || []), { label: "", price: 0 }] } : x));
  const removeOpt = (id, idx) =>
    setItems((arr) => arr.map((x) => x.id === id ? { ...x, options: x.options.filter((_, i) => i !== idx) } : x));

  const addExtraPhoto = async (e, item) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(item.id);
    try {
      await uploadImage(file, async (url) => {
        const next = [...(item.images || []), url];
        await axios.put(`${API}/admin/menu/${item.id}`, { images: next }, H);
        setField(item.id, "images", next);
        toast.success("Photo added.");
      });
    } catch { toast.error("Upload failed."); }
    finally { setSaving(""); e.target.value = ""; }
  };
  const removeExtraPhoto = async (item, idx) => {
    const next = (item.images || []).filter((_, i) => i !== idx);
    try {
      await axios.put(`${API}/admin/menu/${item.id}`, { images: next }, H);
      setField(item.id, "images", next);
      toast.success("Photo removed.");
    } catch { toast.error("Could not remove."); }
  };

  const save = async (item) => {
    setSaving(item.id);
    try {
      await axios.put(`${API}/admin/menu/${item.id}`, {
        name: item.name, price: Number(item.price), category: item.category,
        description: item.description, image: item.image, popular: item.popular,
        options: (item.options || []).filter((o) => o.label.trim()).map((o) => ({ label: o.label, price: Number(o.price) || 0 })),
        images: item.images || [],
      }, H);
      toast.success(`Saved "${item.name}"`);
    } catch { toast.error("Save failed."); }
    finally { setSaving(""); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}" from the menu?`)) return;
    try {
      await axios.delete(`${API}/admin/menu/${item.id}`, H);
      setItems((arr) => arr.filter((x) => x.id !== item.id));
      toast.success("Item deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const uploadImage = async (file, onDone) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(`${API}/admin/upload-image`, fd, {
      headers: { "X-Admin-Passcode": pass, "Content-Type": "multipart/form-data" },
    });
    onDone(data.url);
  };

  const changePhoto = async (e, item) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(item.id);
    try {
      await uploadImage(file, async (url) => {
        await axios.put(`${API}/admin/menu/${item.id}`, { image: url }, H);
        setField(item.id, "image", url);
        toast.success("Photo updated.");
      });
    } catch { toast.error("Photo upload failed."); }
    finally { setSaving(""); e.target.value = ""; }
  };

  const addItem = async () => {
    if (!draft.name.trim()) { toast.error("Enter a name."); return; }
    setAdding(true);
    try {
      const { data } = await axios.post(`${API}/admin/menu`, {
        ...draft, price: Number(draft.price) || 0,
      }, H);
      setItems((arr) => [...arr, data]);
      setDraft(blank);
      toast.success(`Added "${data.name}"`);
    } catch { toast.error("Could not add item."); }
    finally { setAdding(false); }
  };

  const inputCls = "w-full rounded-lg border border-line px-3 py-2 text-sm bg-white focus:border-brand focus:outline-none";

  if (loading) return <div className="py-20 text-center text-inkmuted"><Loader2 className="animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="menu-manager">
      {/* Add new item */}
      <div className="bg-white rounded-2xl border border-brand/30 p-5">
        <h3 className="font-serif text-lg font-bold text-ink flex items-center gap-2"><Plus size={18} className="text-brand" /> Add a new item</h3>
        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <input className={inputCls} placeholder="Item name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="new-item-name" />
          <input className={inputCls} type="number" step="0.01" placeholder="Price (e.g. 9.99)" value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })} data-testid="new-item-price" />
          <select className={inputCls} value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} data-testid="new-item-category">
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
            <input type="checkbox" checked={draft.popular} onChange={(e) => setDraft({ ...draft, popular: e.target.checked })} /> Mark as Popular
          </label>
          <textarea className={`${inputCls} sm:col-span-2`} placeholder="Short description" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="new-item-desc" />
          <label className="inline-flex items-center gap-2 text-sm font-semibold text-brand cursor-pointer">
            <UploadCloud size={16} /> {draft.image ? "Photo added ✓" : "Upload photo"}
            <input type="file" accept="image/*" hidden onChange={async (e) => { const f = e.target.files?.[0]; if (f) { try { await uploadImage(f, (url) => setDraft((d) => ({ ...d, image: url }))); toast.success("Photo ready."); } catch { toast.error("Upload failed."); } } }} data-testid="new-item-image" />
          </label>
        </div>
        <button onClick={addItem} disabled={adding} data-testid="add-item-btn" className="mt-4 inline-flex items-center gap-2 bg-brand text-white rounded-full px-6 py-2.5 text-sm font-bold hover:bg-brand-hover transition-colors disabled:opacity-60">
          {adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add item
        </button>
      </div>

      {/* Existing items */}
      <p className="text-sm text-inkmuted">{items.length} items — edit any field then tap <span className="font-semibold text-ink">Save</span>.</p>
      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.id} data-testid={`menu-item-${item.id}`} className="bg-white rounded-2xl border border-line p-4 flex flex-col sm:flex-row gap-4">
            <div className="shrink-0">
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-surface2 border border-line">
                {item.image ? <img src={imgSrc(item.image)} alt={item.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-inkmuted text-xs">No photo</div>}
              </div>
              <label className="mt-2 flex items-center justify-center gap-1 text-xs font-semibold text-brand cursor-pointer">
                <UploadCloud size={13} /> Change
                <input type="file" accept="image/*" hidden onChange={(e) => changePhoto(e, item)} data-testid={`menu-photo-${item.id}`} />
              </label>
            </div>
            <div className="flex-1 grid sm:grid-cols-2 gap-2">
              <input className={inputCls} value={item.name} onChange={(e) => setField(item.id, "name", e.target.value)} data-testid={`menu-name-${item.id}`} />
              <input className={inputCls} type="number" step="0.01" value={item.price} onChange={(e) => setField(item.id, "price", e.target.value)} data-testid={`menu-price-${item.id}`} />
              <select className={inputCls} value={item.category} onChange={(e) => setField(item.id, "category", e.target.value)}>
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
              <label className="inline-flex items-center gap-2 text-sm text-ink cursor-pointer">
                <input type="checkbox" checked={!!item.popular} onChange={(e) => setField(item.id, "popular", e.target.checked)} /> <Star size={14} className="text-gold" /> Popular
              </label>
              <textarea className={`${inputCls} sm:col-span-2`} rows={2} value={item.description} onChange={(e) => setField(item.id, "description", e.target.value)} />
              <div className="sm:col-span-2 rounded-lg bg-surface2 border border-line p-3">
                <p className="text-xs font-bold text-ink uppercase tracking-wide mb-2">Size options (leave empty for single price)</p>
                {(item.options || []).map((o, oi) => (
                  <div key={oi} className="flex gap-2 mb-2" data-testid={`menu-opt-${item.id}-${oi}`}>
                    <input className={inputCls} placeholder="Label (e.g. Medium Plate)" value={o.label} onChange={(e) => setOpt(item.id, oi, "label", e.target.value)} />
                    <input className={`${inputCls} w-28`} type="number" step="0.01" placeholder="Price" value={o.price} onChange={(e) => setOpt(item.id, oi, "price", e.target.value)} />
                    <button onClick={() => removeOpt(item.id, oi)} className="shrink-0 text-red-600 hover:bg-red-50 rounded-lg px-2" aria-label="Remove option"><Trash2 size={15} /></button>
                  </div>
                ))}
                <button onClick={() => addOpt(item.id)} data-testid={`menu-addopt-${item.id}`} className="inline-flex items-center gap-1 text-brand text-sm font-semibold hover:underline"><Plus size={14} /> Add option</button>
              </div>
              <div className="sm:col-span-2 rounded-lg bg-surface2 border border-line p-3">
                <p className="text-xs font-bold text-ink uppercase tracking-wide mb-2">Extra photos (customers can swipe through these)</p>
                <div className="flex flex-wrap gap-2 items-center">
                  {(item.images || []).map((im, ii) => (
                    <div key={ii} className="relative w-16 h-16 rounded-lg overflow-hidden border border-line" data-testid={`menu-extra-${item.id}-${ii}`}>
                      <img src={imgSrc(im)} alt="extra" className="w-full h-full object-cover" />
                      <button onClick={() => removeExtraPhoto(item, ii)} className="absolute top-0 right-0 bg-red-600 text-white p-0.5 rounded-bl" aria-label="Remove"><Trash2 size={11} /></button>
                    </div>
                  ))}
                  <label className="w-16 h-16 rounded-lg border-2 border-dashed border-brand/40 flex items-center justify-center text-brand cursor-pointer hover:bg-brand/5" data-testid={`menu-addphoto-${item.id}`}>
                    <UploadCloud size={18} />
                    <input type="file" accept="image/*" hidden onChange={(e) => addExtraPhoto(e, item)} />
                  </label>
                </div>
              </div>
              <div className="sm:col-span-2 flex gap-2">
                <button onClick={() => save(item)} disabled={saving === item.id} data-testid={`menu-save-${item.id}`} className="inline-flex items-center gap-1.5 bg-brand text-white rounded-full px-5 py-2 text-sm font-bold hover:bg-brand-hover transition-colors disabled:opacity-60">
                  {saving === item.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
                </button>
                <button onClick={() => remove(item)} data-testid={`menu-delete-${item.id}`} className="inline-flex items-center gap-1.5 bg-red-50 text-red-600 rounded-full px-5 py-2 text-sm font-bold hover:bg-red-100 transition-colors">
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
